// Route Code Edited 09/29/2026
/**
 * Route Widget Label Override — 2Modern (Preferred Checkout Value Prop Header)
 *
 * Purpose:
 *   Rewrites the static lead-in phrase in the Route Preferred Checkout
 *   Value Prop Accordion header while preserving the dynamic quote
 *   amount. Both responsive variants are handled:
 *
 *     "Your order is protected for $X.XX"  ->  "Shipping Protection available for $X.XX"
 *     "Order protected for $X.XX"          ->  "Shipping Protection available for $X.XX"
 *
 *   The lead-in text is NOT configurable through Route's widget config/dashboard
 *   for this widget version, so a theme-side DOM override is the only available
 *   path.
 *
 * How it works:
 *   A MutationObserver watches document.body (childList + subtree). The widget
 *   renders in multiple contexts on this store (main cart page and the cart
 *   drawer), so a single body-level observer covers all of them. On each
 *   mutation batch — coalesced with a short setTimeout so bursts collapse into
 *   one run — the callback first does a cheap `[data-route-widget]` presence
 *   check and bails
 *   immediately on pages/states where no widget exists (this is what keeps the
 *   site-wide include inexpensive). When a widget is present it selects all
 *   `[data-route-widget] #pw-coverage-description` elements, walks their TEXT
 *   nodes, and replaces only the static prefix.
 *
 * Why characterData is NOT observed:
 *   Testing on the live widget shows React does NOT rewrite the static lead-in
 *   on a cart refetch — it only re-renders the price. The phrase reverts only on
 *   a full widget REMOUNT (wrapper torn out and rebuilt), which is a childList
 *   mutation. Observing characterData on the whole body fired the callback on
 *   every text change anywhere on the page for no coverage benefit, so it has
 *   been removed. A brief flash is still possible on a remount (expected,
 *   cosmetic); the observer re-applies immediately after.
 *
 * Idempotency / loop prevention:
 *   After a replacement, neither source phrase remains and the replacement text
 *   contains neither source phrase, so repeated callbacks are no-ops. Rules are
 *   case-sensitive, non-overlapping, and mutually exclusive.
 *
 * Debug logging (opt-in, off by default):
 *   All logging is silent unless debug mode is turned on. Enable it either by
 *   setting `window.__routeCoverageLabelDebug = true` before this script runs,
 *   or by adding `?routeLabelDebug=1` (or `&routeLabelDebug=1`) to the URL.
 *   When enabled, the script logs:
 *     - any caught exception (which would otherwise fail silently), and
 *     - a ONE-TIME warning if a `#pw-coverage-description` element renders text
 *       that matches neither a source phrase nor the already-applied replacement.
 *       That is the signal that a future widget release changed the copy and the
 *       override has silently stopped matching (see Durability risk below).
 *   Debug mode only adds console output; it never changes what the script does
 *   to the page, so it is safe to leave the gate in place in production.
 *
 * Note for reviewers (Route IE process):
 *   Route guidance says not to add a MutationObserver for widget re-init — that
 *   rule prevents double-mounting the widget. This observer never mounts,
 *   unmounts, or re-inits the widget; it only rewrites display text. It does not
 *   call into Route and cannot cause double-init.
 *
 * Durability risk (documented in the Code Install Guide):
 *   `#pw-coverage-description` and the `pw-coverage-description-*` classes are
 *   Route-internal markup, not part of Route's documented data-attribute
 *   contract. A future widget release may change them, at which point the
 *   override silently stops and the label reverts to Route's default text. The
 *   opt-in debug warning above is the diagnostic for exactly this case.
 *
 *   This override also assumes Route keeps the prefix and price in SEPARATE text
 *   nodes and Paired layout (`[data-route-widget]`). A switch to Standard layout
 *   (header moves to `[data-route-widget-header]`) or a React change that merges
 *   the text nodes would also break it silently.
 *
 * Failure behavior:
 *   All work is wrapped in try/catch. On any failure the original Route text is
 *   left untouched — the store reverts to default, unmodified widget output.
 *
 * Revert:
 *   Remove the <script> tag referencing this file. This immediately disables the
 *   script and the widget reverts to its default output. Optionally delete the
 *   file afterward.
 *
 */
(function () {

  // Guard against accidental double-inclusion (two <script> references).
  if (window.__routeCoverageLabelOverrideInit) return;
  window.__routeCoverageLabelOverrideInit = true;

  // Debug gate. Off by default. Enable via window flag (set before this script
  // runs) or the routeLabelDebug URL param. Resolved once at load; wrapped in
  // try/catch so a hardened environment (no window.location, etc.) can't break
  // initialization.
  var DEBUG = false;
  try {
    DEBUG = window.__routeCoverageLabelDebug === true ||
            /[?&]routeLabelDebug=1(?:&|$)/.test(window.location.search);
  } catch (e) { /* leave DEBUG false */ }

  function warn() {
    if (!DEBUG) return;
    try {
      var args = ['[RouteCoverageLabelOverride]'].concat(
        Array.prototype.slice.call(arguments)
      );
      console.warn.apply(console, args);
    } catch (e) { /* never let logging throw */ }
  }

  var REPLACEMENTS = [
    ['Your order is protected for', 'Shipping Protection available for'],
    ['Order protected for', 'Shipping Protection available for']
  ];

  // Text every rule resolves to. Used only by the debug "unrecognized copy"
  // check to tell "already applied" apart from "Route changed the copy."
  var APPLIED_MARKER = 'Shipping Protection available for';

  // One-time-per-session latch so the unrecognized-copy warning doesn't spam
  // the console (the observer callback runs on every mutation batch).
  var unrecognizedWarned = false;

  function overrideCoverageDescription() {
    try {
      // Cheap bail: no widget on this page/state -> do nothing.
      if (!document.querySelector('[data-route-widget]')) return;

      // id is duplicated across widget instances/breakpoints — use querySelectorAll.
      var headers = document.querySelectorAll('[data-route-widget] .pw-coverage-description');
      headers.forEach(function (header) {
        var matchedOrApplied = false;
        var walker = document.createTreeWalker(header, NodeFilter.SHOW_TEXT, null);
        var node;
        while ((node = walker.nextNode())) {
          // Track whether this header already shows our applied text, so the
          // debug check below doesn't false-positive on steady state.
          if (node.nodeValue.indexOf(APPLIED_MARKER) !== -1) {
            matchedOrApplied = true;
          }
          for (var i = 0; i < REPLACEMENTS.length; i++) {
            if (node.nodeValue.indexOf(REPLACEMENTS[i][0]) !== -1) {
              node.nodeValue = node.nodeValue.replace(REPLACEMENTS[i][0], REPLACEMENTS[i][1]);
              matchedOrApplied = true;
              break; // one rule per node; source phrases are mutually exclusive
            }
          }
        }

        // Debug-only: the widget rendered a coverage description we neither
        // rewrote nor had already rewritten. Most likely Route changed the
        // lead-in copy, so this override is now a silent no-op. Warn once.
        if (DEBUG && !matchedOrApplied && !unrecognizedWarned) {
          unrecognizedWarned = true;
          warn(
            'Unrecognized coverage-description copy — no source phrase matched ' +
            'and the applied marker is absent. Route may have changed the label; ' +
            'the override may need updating. Text seen:',
            header.textContent
          );
        }
      });
    } catch (e) {
      // Original text remains untouched. Surfaced only when debug is enabled.
      warn('overrideCoverageDescription failed:', e);
    }
  }

  function attachRouteObserver() {
    try {
      var root = document.body;
      if (!root) return;

      overrideCoverageDescription(); // catch any widget already present at load

      // Coalesce bursts of mutations into a single run. setTimeout is used
      // deliberately instead of requestAnimationFrame: rAF callbacks are paused
      // while the tab is not visible, which would delay the override until the
      // tab regains focus. setTimeout fires regardless of visibility.
      var scheduled = false;
      function schedule() {
        if (scheduled) return;
        scheduled = true;
        setTimeout(function () {
          scheduled = false;
          overrideCoverageDescription();
        }, 0);
      }

      var observer = new MutationObserver(schedule);

      // childList + subtree only. characterData intentionally omitted — the
      // phrase reverts only on remount (a childList change), not on refetch.
      observer.observe(root, {
        childList: true,
        subtree: true
      });

      warn('observer attached; debug logging enabled.');
    } catch (e) {
      warn('attachRouteObserver failed:', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachRouteObserver);
  } else {
    attachRouteObserver();
  }

})();
// End Route Code